import React, { createContext, useContext, useMemo } from 'react';
import { useAttentionTracker, type AttentionTrackerAPI } from '@/hooks/analytics/useAttentionTracker';

interface AttentionContextValue extends AttentionTrackerAPI {
  interviewId: string | null;
  isEnabled: boolean;
}

const AttentionContext = createContext<AttentionContextValue | null>(null);

interface AttentionProviderProps {
  children: React.ReactNode;
  interviewId: string;
  userId?: string;
  enabled?: boolean;
}

export function AttentionProvider({
  children,
  interviewId,
  userId,
  enabled = true,
}: AttentionProviderProps) {
  const tracker = useAttentionTracker({
    interviewId,
    userId,
    enabled,
  });

  const value = useMemo<AttentionContextValue>(
    () => ({
      ...tracker,
      interviewId,
      isEnabled: enabled,
    }),
    [tracker, interviewId, enabled]
  );

  return (
    <AttentionContext.Provider value={value}>
      {children}
    </AttentionContext.Provider>
  );
}

export function useAttention(): AttentionContextValue | null {
  return useContext(AttentionContext);
}

// Hook that throws if used outside provider (for required contexts)
export function useAttentionRequired(): AttentionContextValue {
  const context = useContext(AttentionContext);
  if (!context) {
    throw new Error('useAttentionRequired must be used within an AttentionProvider');
  }
  return context;
}
